import React, { type FC, type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Divider, Paper, Stack, useTheme } from '@mui/material';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { bindTrigger } from 'material-ui-popup-state/hooks';

import ChevronDown from '@/assets/icons/chevron-down.svg?react';
import ResetIcon from '@/assets/icons/reset.svg?react';
import Loader from '@/components/Loader/Loader';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { useDataControls } from '@/hooks/useDataControls';
import { useDirection } from '@/hooks/useDirection';
import { getCompoundValuesForSection } from '@/hooks/useEntityList';
import { useQuerySuspender } from '@/hooks/useQuerySuspender';
import { transforms, transitions } from '@/theme/constants';
import typography from '@/theme/typography';
import { formChangeCounter } from '@/utils/formChangeCounter';
import { formStateFallback } from '@/utils/formStateFallback';

interface DropdownSectionProps {
  title: string;
  children: ReactNode;
  name: string;
}

const DropdownSection: FC<DropdownSectionProps> = ({ title, children, name }) => {
  const { compoundKeys: compoundFormKeys } = useDataControls();
  const { applyDirectionalStyles, directionalSpacing } = useDirection();
  const [isOpen, setOpen] = useState(false);
  const { t } = useTranslation('filters');
  const { spacing, palette } = useTheme();
  const popperRef = useRef();

  useQuerySuspender(isOpen);

  const { reset, getValues, formState, watch } = useFormContext();
  const [filterFormValue, setFilterFormValue] = useState(formState.defaultValues as object);

  useEffect(() => {
    const subscription = watch((value: object) => setFilterFormValue(value));

    return () => subscription.unsubscribe();
  }, [watch]);

  const filterChangeCounter = useMemo(
    () =>
      formChangeCounter(_get(filterFormValue, name), {
        defaultValues: _get(formState?.defaultValues, name),
        compoundEntriesPaths: getCompoundValuesForSection(name, compoundFormKeys || [])
      }),
    [filterFormValue, name, formState?.defaultValues, compoundFormKeys]
  );

  const handleReset = useCallback(() => {
    const values = _cloneDeep(getValues());
    const presentValue = _get(values, name);
    const defaultValue = _get(formState.defaultValues, name);
    const fallbackValue = formStateFallback(presentValue, defaultValue);
    _set(values, name, fallbackValue);
    reset(values);
  }, [getValues, name, formState.defaultValues, reset]);

  const handleClose = useCallback(() => {
    (popperRef.current! as { close?: () => void })?.close?.();
  }, []);

  const handleToggle = useCallback((isOpen: boolean) => setOpen(isOpen), []);

  return (
    <StandardPopper
      ref={popperRef}
      onToggle={handleToggle}
      placement="bottom-start"
      getSx={() => ({
        mt: 0.25,
        '& .MuiPaper-root': {
          overflow: 'visible',
          bgcolor: 'primary.50',
          '&::before': {
            display: 'none'
          },
          '&::after': {
            display: 'none'
          }
        }
      })}
      content={
        <Paper>
          <Stack>
            <Stack maxHeight="56vh" overflow="auto" width="298px">
              <Suspense
                fallback={
                  <Stack justifyContent="center" alignItems="center" width="100%" minHeight="200px">
                    <Loader />
                  </Stack>
                }
              >
                {children}
              </Suspense>
            </Stack>
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                p: 1.25,
                bgcolor: 'background.default',
                '.MuiButton-root': {
                  ...typography.body3,
                  fontWeight: typography.fontWeightMedium,
                  p: spacing(0, 1)
                },
                '.MuiButton-icon': {
                  width: spacing(2)
                }
              }}
            >
              <Button startIcon={<ResetIcon />} sx={{ color: 'primary.400' }} onClick={handleReset}>
                {t('filters.reset')}
              </Button>
              <Button variant="contained" sx={{ minWidth: 'auto' }} onClick={handleClose}>
                {t('filters.close')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      }
      noTrigger
    >
      {popupState => (
        <Button
          {...bindTrigger(popupState)}
          endIcon={<ChevronDown className="ExpandIcon-root" />}
          sx={{
            color: 'grey.900',
            p: directionalSpacing(0.75, 0.5, 0.75, 1),
            ...applyDirectionalStyles(0, 3, 'ml', 'mr'),
            ...typography.body3,
            fontWeight: typography.fontWeightMedium,
            lineHeight: spacing(2),
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'transparent',
              '& .ExpandIcon-root': {
                color: 'primary.main'
              }
            },
            '& .MuiButton-endIcon': applyDirectionalStyles(1, 0, 'ml', 'mr'),
            '& .ExpandIcon-root': {
              width: spacing(2),
              height: spacing(2),
              transform: transforms.mirrorY(popupState.isOpen),
              transition: transitions.transform
            },
            ...(popupState.isOpen &&
              !filterChangeCounter && {
                color: 'primary.600'
              }),
            ...(!popupState.isOpen &&
              !!filterChangeCounter && {
                color: 'primary.1000'
              }),
            ...(popupState.isOpen &&
              !!filterChangeCounter && {
                color: 'primary.700'
              })
          }}
        >
          {title}
          {!!filterChangeCounter && (
            <Chip
              sx={{
                mt: -0.25,
                mb: -0.25,
                ...applyDirectionalStyles(0.5, 0, 'ml', 'mr'),
                p: spacing(0, 0.5),
                height: spacing(2.5),
                bgcolor: 'primary.main',
                color: 'background.default',
                '& .MuiChip-deleteIcon': {
                  m: spacing(0.375),
                  color: palette.background.default,
                  '&:hover': {
                    color: palette.background.default
                  }
                },
                '& .MuiChip-label': {
                  p: 0,
                  ...applyDirectionalStyles(0.5, 0, 'pl', 'pr'),
                  ...typography.body3,
                  fontWeight: typography.fontWeightMedium
                },
                '& .ResetIcon-root': {
                  width: spacing(1.5),
                  height: spacing(1.5)
                }
              }}
              label={filterChangeCounter}
              onDelete={handleReset}
              deleteIcon={<ResetIcon className="ResetIcon-root" />}
            />
          )}
        </Button>
      )}
    </StandardPopper>
  );
};

export default DropdownSection;
